
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, TextField, Checkbox, FormControlLabel,
    Button, CircularProgress, Divider
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiUrl from '../../Config';
import { toast } from 'react-toastify';

const AddEditCategory = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isEditMode, initialData } = location.state || {};

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
        categoriesImg: null, // This will hold the File object or null
        isActive: false,
        isPopular: false,
        sequenceOrder: '',
    });

    const [imagePreview, setImagePreview] = useState(null); // URL or null
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});


    // Populate form on edit
    useEffect(() => {
        if (initialData) {
            setFormData({
                id: initialData.id || '',
                name: initialData.name || '',
                description: initialData.description || '',
                categoriesImg: null, // we don't set file here, just null, upload only if user selects
                isActive: initialData.isActive || false,
                isPopular: initialData.isPopular || false,
                sequenceOrder: initialData.sequenceOrder || '',
            });

            // If image URL is present (string), set as preview
            if (initialData.categoriesImg && typeof initialData.categoriesImg === 'string') {
                setImagePreview(initialData.categoriesImg);
            } else if (initialData.imageUrl) {
                // Or if imageUrl prop exists in your data
                setImagePreview(initialData.imageUrl);
            }
        }
    }, [initialData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError('');
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, categoriesImg: file }));
            setImagePreview(URL.createObjectURL(file));
            setError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let errors = {};

        if (!formData.name.trim()) errors.name = 'Name is required.';
        if (!formData.description.trim()) errors.description = 'Description is required.';
        if (!formData.sequenceOrder) errors.sequenceOrder = 'Sequence Order is required.';
        if (!imagePreview) errors.categoriesImg = 'Please upload an image.';

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setLoading(true);
        setFieldErrors({});
        setError('');

        try {
            const payload = new FormData();
            payload.append('id', formData.id);
            payload.append('name', formData.name);
            payload.append('description', formData.description);
            if (formData.categoriesImg instanceof File) {
                payload.append('categoriesImg', formData.categoriesImg);
            } else if (typeof imagePreview === 'string') {
                payload.append('categoriesImgUrl', imagePreview);
            }
            payload.append('isActive', formData.isActive);
            payload.append('isPopular', formData.isPopular);
            payload.append('sequenceOrder', formData.sequenceOrder);

            const response = isEditMode
                ? await axios.put(`${apiUrl}/categories/updatecategories/${formData.id}`, payload)
                : await axios.post(`${apiUrl}/categories/createcategories`, payload);

            if (response.data.status === true) {
                toast.success(isEditMode ? 'Category updated successfully!' : 'Category added successfully!');
                navigate(-1);
            } else {
                setError('Something went wrong on the server.');
            }
        } catch (error) {
            const message = error?.response?.data?.message || error.message || 'Failed to fetch enquiries'
            setError(message)
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="h4" fontWeight={400} mb={2} mt={1}>
                {isEditMode ? `Edit Category - ${formData.name}` : 'Add New Category'}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>

                    {/* Image Upload */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" fontWeight={500} gutterBottom>
                            Category Image
                        </Typography>
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: 2,
                                bgcolor: 'primary.background',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                // border: '1px dashed gray',
                                mb: 1,
                            }}
                        >
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    style={{ width: '110px', height: '110px', borderRadius: 4 }}
                                />
                            ) : (
                                <Typography variant="caption">No Image</Typography>
                            )}
                        </Box>

                        <label htmlFor="upload-img">
                            <input
                                type="file"
                                id="upload-img"
                                hidden
                                accept="image/*"
                                onChange={handleImageFileChange}
                            />
                            <Button variant="outlined" component="span" type="button">
                                Upload Image
                            </Button>
                            {fieldErrors.categoriesImg && (
                                <Typography color="error" variant="caption">
                                    {fieldErrors.categoriesImg}
                                </Typography>
                            )}
                        </label>
                    </Grid> <br />


                    {/* Name */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Name"
                            name="name"
                            fullWidth
                            size="small"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={Boolean(fieldErrors.name)}
                            helperText={fieldErrors.name}
                        />
                    </Grid> <br />

                    {/* Description */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Description"
                            name="description"
                            fullWidth
                            size="small"
                            value={formData.description}
                            onChange={handleInputChange}
                            error={Boolean(fieldErrors.description)}
                            helperText={fieldErrors.description}
                        />
                    </Grid> <br />

                    {/* Sequence Order */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Sequence Order"
                            name="sequenceOrder"
                            fullWidth
                            size="small"
                            value={formData.sequenceOrder}
                            onChange={handleInputChange}
                            error={Boolean(fieldErrors.sequenceOrder)}
                            helperText={fieldErrors.sequenceOrder}
                        />
                    </Grid> <br />

                    {/* Checkboxes */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleCheckboxChange}
                                    size="small"
                                />
                            }
                            label="Is Active"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="isPopular"
                                    checked={formData.isPopular}
                                    onChange={handleCheckboxChange}
                                    size="small"
                                />
                            }
                            label="Is Popular"
                        />
                    </Grid> <br />

                    {/* Error Message */}
                    {error && (
                        <Grid item xs={12}>
                            <Typography color="error" textAlign="left">
                                {error}
                            </Typography>
                        </Grid>
                    )}

                    {/* Submit Button */}
                    <Grid size={{ xs: 12 }}>
                        <Box display="flex" justifyContent="end" mt={2} gap={2}>
                            <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" disabled={loading}>
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                                ) : (
                                    isEditMode ? 'Update' : 'Add'
                                )}
                            </Button>
                        </Box>
                    </Grid>

                </Grid>
            </form>
        </Box>
    );
};

export default AddEditCategory;

